/// <amd-module name="Dashboards/DashboardTemplates/DashboardTemplatePreview" />
import * as React from "react";

import * as Resources from "Dashboards/DashboardTemplates/Resources";

import { getTemplateData, getTemplatePreviewImages, getTemplatePreviewText, TemplatePreviewText, TemplateWidgetDescription } from "./TemplateData";

import { VssComponent } from "VSS/Platform/Layout";
import { getAssetLocation } from "VSS/Platform/Location";
import { ClientTheme } from "VSS/Platform/Theming";
import { Dialog, IDialogProps } from "VSSUI/Dialog";
import { TitleSize } from "VSSUI/Header";
import { Image } from "VSSUI/Image";
import { Tooltip } from "VSSUI/TooltipEx";

export interface DashboardTemplatePreviewProps {
    onDismissClick: () => void;
    onCreateDashboardTemplateClick: () => void;
    theme: ClientTheme;
    templateType: string;
    noneSelected: string;
    widgetDescriptions: TemplateWidgetDescription[];
}

export class DashboardTemplatePreview extends VssComponent<DashboardTemplatePreviewProps> {
    public render(): JSX.Element | null {
        if (this.props.templateType === this.props.noneSelected) {
            return null;
        }

        const templateText = this.getTemplatePreviewText();
        const dialogProps: IDialogProps = {
            onDismiss: this.props.onDismissClick,
            className: "dashboard-template-preview-dialog",
            lightDismiss: true,
            modal: true,
            contentSize: 3,
            titleProps: {
                text: templateText.title,
                size: TitleSize.Large
            },
            footerButtonProps: [
                {
                    text: Resources.DashboardTemplatePage_CancelText,
                    onClick: this.props.onDismissClick,
                    subtle: true
                },
                {
                    text: Resources.DashboardTemplatePage_CreateText,
                    onClick: this.props.onCreateDashboardTemplateClick,
                    primary: true
                }
            ]
        };

        return (
            <Dialog {...dialogProps}>
                <div id={templateText.styleId}>
                    <div className="grid">{this.renderTemplatePreviewImages(this.props.templateType)}</div>
                    <div tabIndex={0} className="template-preview-description">
                        {templateText.description}
                        <div tabIndex={0} className="template-preview-widget-list">
                            {Resources.DashboardTemplatePage_Preview_WidgetList_Title}
                            {this.getWidgetNameList(this.props.templateType)}
                        </div>
                    </div>
                </div>
            </Dialog>
        );
    }

    private getWidgetNameList = (requestedTemplate: string): JSX.Element[] => {
        let widgetNameList: JSX.Element[] = [];
        const checkWidgetNames = getTemplateData().find(template => template.type === requestedTemplate);
        if (checkWidgetNames !== undefined) {
            const generateWidgetNames = checkWidgetNames;
            widgetNameList = generateWidgetNames.widgets.map(widgetData => {
                const templateWidgetText = this.getTemplateWidgetText(widgetData.contributionId);
                return (
                    <Tooltip key={widgetData.id} text={templateWidgetText.description}>
                        <div tabIndex={0}>{templateWidgetText.name}</div>
                    </Tooltip>
                );
            });
        }
        return widgetNameList;
    };

    /** Populates the images for the template preview */
    private renderTemplatePreviewImages = (requestedTemplate: string): JSX.Element[] => {
        let templateImages: JSX.Element[] = [];
        const checkTemplateImages = getTemplatePreviewImages().find(template => template.type === requestedTemplate);
        if (checkTemplateImages !== undefined) {
            const generateTemplateImages = checkTemplateImages;
            templateImages = generateTemplateImages.images.map(image => {
                const widgetText = this.getTemplateWidgetText(image.contributionId);
                const themeUrl: string = this.props.theme.isDark ? image.previewDark : image.previewLight;
                const widgetInfo = `${widgetText.name}: ${widgetText.description}`;
                return (
                    <Tooltip key={widgetText.name} text={widgetInfo}>
                        <div tabIndex={0}>
                            <Image
                                src={getAssetLocation(this.context.pageContext, themeUrl)}
                                alt={widgetInfo}
                                className="preview-img"
                                containImage={true}
                            />
                        </div>
                    </Tooltip>
                );
            });
        }
        return templateImages;
    };

    /** Helper for renderTemplatePreviewImages that gets a widget's name and description to display upon hovering on the preview image */
    private getTemplateWidgetText = (contributionId: string): TemplateWidgetDescription => {
        const widgetDescription = this.props.widgetDescriptions.find(widget => widget.contributionId === contributionId);
        if (widgetDescription !== undefined) {
            return widgetDescription;
        } else {
            return { contributionId: contributionId, name: "", description: "" };
        }
    };

    private getTemplatePreviewText = (): TemplatePreviewText => {
        const templatePreviewText = getTemplatePreviewText().find(templateText => templateText.type === this.props.templateType);
        if (templatePreviewText !== undefined) {
            return templatePreviewText;
        } else {
            return { type: "", styleId: "", title: "", description: "" };
        }
    };
}
